import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Grid,
  GridColumn as Column,
  GridToolbar,
} from "@progress/kendo-react-grid";
import {
  CustomActionsCell,
  CustomCell,
  HeaderCustomCell,
  initialDataState,
} from "./configurable-task-util";
import { process } from "@progress/kendo-data-query";
import { TaskGridFilter } from "../common-components/task-grid-filter";
import PropTypes from "prop-types";
import { FluentInput } from "../../../../../components/FluentUI/Fluent-Input";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import _ from "lodash";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  setGroupIds,
  setExpandedState,
  getGroupIds,
} from "@progress/kendo-react-data-tools";
import { LoginContext } from "../../../../../services/api/authentication-api";

const processWithGroups = (data, dataState) => {
  const newDataState = process(data, dataState);
  setGroupIds({ data: newDataState.data, group: dataState.group });
  return newDataState;
};

// Legacy Grid Component for Configurable Task List
// To avoid potential issues with the new grid component
// This supports the older uses cases. E.g. Libro, Intranet, RBC-GIB, etc.
// Note: No changes were made to the grid component itself.
export const ConfigurableTaskGridLegacy = ({ data, type, GetTasks }) => {
  const _export = useRef(null);
  const user = useContext(LoginContext);
  const labels = user?.objectLabels;
  const savedGridDefination = JSON.parse(
    localStorage.getItem("gridState_" + user?.userID)
  );
  const savedGridColumnDefination = JSON.parse(
    localStorage.getItem("gridColumns_" + user?.userID)
  );
  const [savedSearch, setSavedSearch] = useState(
    localStorage.getItem("gridSearch_" + user?.userID) ?? ""
  );

  const [dataState, setDataState] = useState(
    savedGridDefination ?? initialDataState
  );
  const [dataResult, setDataResult] = useState(process([], dataState));
  const [collapsedState, setCollapsedState] = useState([]);
  const [columns, setColumns] = useState(
    savedGridColumnDefination ?? data.allColumns
  );

  const onDataStateChange = (e) => {
    if (e.dataState.group) {
      e.dataState.group = e.dataState.group.filter(
        (obj, index) =>
          e.dataState.group.findIndex((item) => item.field === obj.field) ===
          index
      );
    }
    let tempSkip = e.dataState.skip;
    e.dataState.skip = 0;
    localStorage.setItem(
      "gridState_" + user?.userID,
      JSON.stringify(e.dataState)
    );
    e.dataState.skip = tempSkip;
    setDataState(e.dataState);
    setDataResult(processWithGroups(data.data, e.dataState));
  };

  const onExpandChange = useCallback(
    (event) => {
      const item = event.dataItem;
      if (item.groupId) {
        const collapsedIds = !event.value
          ? [...collapsedState, item.groupId]
          : collapsedState.filter((groupId) => groupId !== item.groupId);
        setCollapsedState(collapsedIds);
      }
    },
    [collapsedState]
  );

  const onColumnReorder = (event) => {
    let col = event.columns
      .filter((column) => column.title !== "Actions")
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((column) => column.field.split(".")[0]);
    setColumns(col);
    localStorage.setItem("gridColumns_" + user?.userID, JSON.stringify(col));
  };

  const onColumnResize = (event) => {
    let col = event.columns
      .filter((column) => column.title !== "Actions")
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((column) => column.field.split(".")[0]);
    setColumns(col);
    localStorage.setItem("gridColumns_" + user?.userID, JSON.stringify(col));
  };

  const onGroupsToggle = useCallback(() => {
    const dataStateGroups = processWithGroups(data.data, {
      group: dataState.group,
    });
    setCollapsedState(
      collapsedState.length ? [] : getGroupIds({ data: dataStateGroups.data })
    );
  }, [collapsedState, data.data, dataState]);

  const expandedResult = setExpandedState({
    data: dataResult.data,
    collapsedIds: collapsedState,
  });

  useEffect(() => {
    if (data && data.data && data.data.length > 0) {
      setDataResult(process(data.data, dataState));
    } else {
      setDataResult(process([], dataState));
    }
  }, []);

  const resetDataState = () => {
    setDataState(initialDataState);
    setDataResult(process(data.data, initialDataState));
    setColumns(data.allColumns);
    setSavedSearch("");
    localStorage.removeItem("gridState_" + user?.userID);
    localStorage.removeItem("gridColumns_" + user?.userID);
    localStorage.removeItem("gridSearch_" + user?.userID);
  };

  const FilterData = (e) => {
    let value = e.target.value;
    let modifiedDataState = { ...dataState };

    let searchFilter = [];

    if (value !== "") {
      searchFilter = data.allColumns.map((column) => ({
        field: `${column}.SearchableData`,
        operator: "contains",
        value: value,
      }));

      searchFilter.push({
        field: "SearchableMembers.SearchableData",
        operator: "contains",
        value: value,
      });
    }

    modifiedDataState.filter = {
      logic: "or",
      filters: searchFilter,
    };
    let tempSkip = modifiedDataState.skip;
    modifiedDataState.skip = 0;
    localStorage.setItem(
      "gridState_" + user?.userID,
      JSON.stringify(modifiedDataState)
    );
    localStorage.setItem("gridSearch_" + user?.userID, value);
    modifiedDataState.skip = tempSkip;
    setDataState(modifiedDataState);
    setDataResult(process(data.data, modifiedDataState));
  };

  const debouncedFilter = _.debounce(FilterData, 200);

  const excelExport = async () => {
    if (_export.current !== null) {
      let exportData = { data: data?.data, total: data?.data.length };

      if (dataState.filter || dataState.group) {
        let dState = { ...dataState };
        dState.take = dataResult.length;
        exportData = process(data?.data, dState);
      }

      if (dataState.group && exportData.data) {
        const flattenGroupedData = (group) =>
          group.reduce((acc, item) => {
            if (item.items) {
              return acc.concat(flattenGroupedData(item.items));
            }
            return acc.concat(item);
          }, []);

        exportData.data = flattenGroupedData(exportData.data);
      }

      await _export.current.save(exportData);
    }
  };

  return (
    <div className="configurable-list-container">
      <ExcelExport data={dataResult.data} ref={_export}>
        {data?.allColumns?.map((column) => {
          let newColumn =
            column === "Assigned Members" ? "SearchableMembers" : column;
          return (
            <ExcelExportColumn
              key={newColumn}
              field={`${newColumn}.SearchableData`}
              title={data?.data?.[0]?.[column]?.Title}
            />
          );
        })}
      </ExcelExport>
      <Grid
        data={expandedResult}
        pageable={{
          info: true,
          pageSizes: [10, 25, 50, 75, 100],
        }}
        total={dataResult.total}
        groupable={true}
        reorderable={true}
        resizable={true}
        {...dataState}
        onDataStateChange={onDataStateChange}
        onColumnReorder={onColumnReorder}
        onColumnResize={onColumnResize}
        expandField="expanded"
        onExpandChange={onExpandChange}
        cells={{
          group: {
            groupHeader: (props) => HeaderCustomCell(props, user),
          },
        }}
      >
        <GridToolbar>
          <div className="task-grid-toolbar">
            <div className="toolbar-left">
              <FluentInput
                placeholder={`Search in all ${type} ${labels?.TaskPlural ?? 'Tasks'}`}
                defaultValue={savedSearch}
                contentBefore={
                  <FluentUiIcon
                    name={"SearchRegular"}
                    color={"#0078D4"}
                    size={14}
                  />
                }
                onChange={(e) => debouncedFilter(e)}
              />
              <FluentButton
                text="Export to XLS"
                onClick={excelExport}
              ></FluentButton>
            </div>
            <div className="toolbar-right">
              <FluentButton
                text="Reset to Default"
                icon={
                  <FluentUiIcon
                    name={"ArrowResetRegular"}
                    color={"#242424"}
                    size={20}
                  />
                }
                onClick={resetDataState}
              ></FluentButton>
              <FluentButton
                text="Refresh"
                icon={
                  <FluentUiIcon
                    name={"ArrowClockwiseRegular"}
                    color={"#242424"}
                    size={20}
                  />
                }
                onClick={GetTasks}
              ></FluentButton>
            </div>
          </div>
        </GridToolbar>
        {columns?.map(
          (column, index) =>
            data?.data?.[0]?.[column]?.Hidden === false && (
              <Column
                key={column}
                field={`${column}.Data`}
                orderIndex={index}
                title={
                  data?.data?.[0]?.[column]
                    ? data?.data?.[0]?.[column]?.Title
                    : column
                }
                locked={data?.data?.[0]?.[column]?.Locked}
                groupable={data?.data?.[0]?.[column]?.Groupable}
                filter="date"
                columnMenu={
                  data?.data?.[0]?.[column]?.Filterable &&
                  data?.data?.[0]?.[column]?.Sortable
                    ? (props) => {
                        return TaskGridFilter(props, data);
                      }
                    : null
                }
                cells={{
                  data: (props) => CustomCell(column, props),
                }}
              />
            )
        )}
        <Column
          orderIndex={columns?.length ?? 0}
          reorderable={false}
          field=""
          title="Actions"
          cells={{
            data: (props) => CustomActionsCell(props, GetTasks),
          }}
        />
      </Grid>
    </div>
  );
};

ConfigurableTaskGridLegacy.propTypes = {
  data: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  GetTasks: PropTypes.func.isRequired,
};